"use client";

import type { FC } from "react";
import type { DemoConfig } from "@/app/_blocks/BlockOmniboxSlides";

import dynamic from "next/dynamic";

import type { Voice } from "@mw/shared/payload-types";
import { assertNever } from "@mw/shared/asserts";
import { filterEntitiesArray } from "@utilities/payload";

const TextToSpeechDemo = dynamic(() => import("./TextToSpeechDemo"));
const SideBySideDemo = dynamic(() => import("./SideBySideDemo"));
const ImageVideoDemo = dynamic(() => import("./ImageVideoDemo"));
const DubbingVideoDemo = dynamic(() => import("./DubbingVideoDemo"));
const VoiceDesignDemo = dynamic(() => import("./VoiceDesignDemo"));
const SFXScrollerDemo = dynamic(() => import("./SFXScrollerDemo"));
const MusicDemo = dynamic(() => import("./MusicDemo"));
const STTOmniboxDemo = dynamic(() => import("./STTOmniboxDemo"));
const AudioBookDemo = dynamic(() => import("./AudioBookDemo"));
const ExternalModelDemo = dynamic(() => import("./ExternalModelDemo"));
const AgentOrbDemo = dynamic(() =>
  import("@/app/_blocks/BlockBentoGrid/AgentOrbDemo").then(mod => ({ default: mod.AgentOrbDemo })),
);

export interface DemoContentProps {
  demo: DemoConfig;
}

export const DemoContent: FC<DemoContentProps> = ({ demo }) => {
  switch (demo.template) {
    case "text-to-speech":
      return <TextToSpeechDemo defaultText={demo["text-to-speech"]?.startingSentence || ""} />;
    case "voice-cloning": {
      const voiceCloning = demo["voice-cloning"];
      return (
        <SideBySideDemo
          leftDisc={voiceCloning.leftDisc}
          rightDisc={voiceCloning.rightDisc}
          trackingSource='voice_cloning_demo'
        />
      );
    }
    case "voice-changer": {
      const voiceChanger = demo["voice-changer"];
      return (
        <SideBySideDemo
          leftDisc={voiceChanger.leftDisc}
          rightDisc={voiceChanger.rightDisc}
          trackingSource='voice_changer_demo'
        />
      );
    }
    case "voice-designer": {
      const voiceDesigner = demo["voice-designer"];
      return <VoiceDesignDemo voices={voiceDesigner.voices} />;
    }
    case "voice-isolator": {
      const voiceIsolator = demo["voice-isolator"];
      return (
        <SideBySideDemo
          leftDisc={voiceIsolator.leftDisc}
          rightDisc={voiceIsolator.rightDisc}
          trackingSource='voice_isolator_demo'
        />
      );
    }
    case "dubbing": {
      const dubbing = demo["dubbing"];
      return <DubbingVideoDemo video={dubbing.video} dubbingTracks={dubbing.dubbingTracks} />;
    }
    case "image-and-video":
      return <ImageVideoDemo />;
    case "external-model": {
      const externalModel = demo["external-model"];
      const models = Array.isArray(externalModel?.models)
        ? externalModel.models.filter((m): m is string => typeof m === "string")
        : undefined;
      return (
        <ExternalModelDemo
          step={externalModel?.step ?? "select-model"}
          modelName={externalModel?.modelName}
          prompt={externalModel?.prompt}
          modality={externalModel?.modality}
          models={models}
          assetUrl={externalModel?.assetUrl}
          contentUrl={externalModel?.contentUrl}
        />
      );
    }
    case "sound-effects": {
      const soundEffects = demo["sound-effects"];
      return <SFXScrollerDemo cards={soundEffects.cards} />;
    }
    case "music": {
      const music = demo["music"];
      return <MusicDemo songs={music.songs} trackingSource='music_demo' />;
    }
    case "stt": {
      const stt = demo["stt"];
      return <STTOmniboxDemo samples={stt.samples} />;
    }
    case "audiobook": {
      const audiobook = demo["audiobook"];
      return <AudioBookDemo text={audiobook.text} chapterTitle={audiobook.chapterTitle} voices={audiobook.voices} />;
    }
    case "agent-demo": {
      const agentDemo = demo["agent-demo"];
      const voices = filterEntitiesArray(agentDemo.voices) as Voice[];
      if (!agentDemo.agentId || !voices.length) return null;
      return <AgentOrbDemo agentId={agentDemo.agentId} voices={voices} />;
    }
    default:
      throw assertNever("DemoContent: unknown demoType", demo);
  }
};

export default DemoContent;
